import React from 'react';
import { liCss } from './styles';
import { GeneratedSocialPostResult } from 'src/api/types/social-types';

type Maybe<T> = T | null | undefined;

export interface LiCounters {
  reactions?: number;
  comments?: number;
  reposts?: number;
}

export interface LinkedInPostPreviewProps {
  data: GeneratedSocialPostResult;
  counters?: LiCounters;
  injectCss?: boolean; // set false if you include CSS globally
  profileName?: string;
  profileSubtitle?: string; // e.g. "Building AI Agent · Syntraxis"
  profileAvatarUrl?: string;
  when?: string; // e.g. "1h"
  linkPreview?: {
    url: string;
    image?: string;
    title?: string;
    description?: string;
  };
}

function domainFromUrl(url: Maybe<string>) {
  if (!url) return '';
  try {
    const u = new URL(url);
    return u.host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function linkify(text: string) {
  const urlRegex =
    /((https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/[^\s]*)?)/gi;

  const lines = (text || '').split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {line.split(urlRegex).map((chunk, j) => {
        const looksLikeUrl =
          /^https?:\/\//i.test(chunk) || /^[a-z0-9-]+\.[a-z]{2,}/i.test(chunk);
        if (looksLikeUrl) {
          const href = /^https?:\/\//i.test(chunk) ? chunk : `https://${chunk}`;
          return (
            <a
              key={`${i}-${j}`}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="lip-link">
              {chunk}
            </a>
          );
        }
        return <React.Fragment key={`${i}-${j}`}>{chunk}</React.Fragment>;
      })}
      {i < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ));
}

export default function LinkedInPostPreview({
  data,
  counters,
  injectCss = true,
  profileName,
  profileSubtitle,
  profileAvatarUrl = 'https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg',
  when = '1h',
  linkPreview,
}: LinkedInPostPreviewProps) {
  const post = data.post;
  const reactions = counters?.reactions ?? 0;
  const comments = counters?.comments ?? 0;
  const reposts = counters?.reposts ?? 0;

  const headerName = profileName || post.location?.name || 'Profile Name';
  const subtitle = profileSubtitle || 'Building AI Agent · Syntraxis';

  // Image (standalone post image)
  const postImgSrc: Maybe<string> = data.imageLinkUrl || post.image?.link_url;
  const hasPostImage = Boolean(postImgSrc);

  // Link preview (LinkedIn-style card)
  const hasLink = Boolean(
    linkPreview?.url || post.cta?.url || post.metadata?.has_link,
  );
  const linkUrl = (linkPreview?.url || post.cta?.url || '').trim();
  const cardImg = linkPreview?.image;
  const cardTitle = linkPreview?.title || post.topic || linkUrl;
  const cardDesc = linkPreview?.description || '';

  return (
    <>
      {injectCss && <style>{liCss}</style>}

      <main className="lip-container" role="main">
        <article className="lip-card" aria-label="LinkedIn-like post preview">
          {/* HEADER */}
          <header className="lip-header">
            <img src={profileAvatarUrl} alt="Profile" className="lip-avatar" />
            <div className="lip-header-main">
              <div className="lip-name-row">
                <a className="lip-name" role="button" tabIndex={0}>
                  {headerName}
                </a>
              </div>
              <div className="lip-subtitle">
                <div className="lip-subtitle-line" title={subtitle}>
                  {subtitle}
                </div>
                <div className="lip-subline">
                  <span>{when}</span>
                  <span aria-hidden="true">•</span>
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    className="lip-geo">
                    <path
                      fill="currentColor"
                      d="M12 2a10 10 0 100 20 10 10 0 000-20zm7.93 9h-3.18A15.8 15.8 0 0016 5.33 8.02 8.02 0 0119.93 11zM12 4c.9 0 2.35 1.78 2.97 5H9.03C9.65 5.78 11.1 4 12 4zM8 5.33A15.8 15.8 0 007.25 11H4.07A8.02 8.02 0 018 5.33zM4.07 13h3.18c.17 1.95.75 3.73 1.5 5.67A8.02 8.02 0 014.07 13zM12 20c-.9 0-2.35-1.78-2.97-5h5.94C14.35 18.22 12.9 20 12 20zm3.93-1.33c.75-1.94 1.33-3.72 1.5-5.67h3.18a8.02 8.02 0 01-4.68 5.67z"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <button aria-label="More" className="lip-more" type="button">
              <svg viewBox="0 0 24 24" className="lip-more-icon">
                <circle cx="5" cy="12" r="2" />
                <circle cx="12" cy="12" r="2" />
                <circle cx="19" cy="12" r="2" />
              </svg>
            </button>
          </header>

          {/* BODY */}
          <div className="lip-body">{linkify(post.body || '')}</div>

          {/* LINK PREVIEW */}
          {hasLink && linkUrl && (
            <a
              href={linkUrl}
              target="_blank"
              rel="noreferrer"
              className="lip-link-card">
              {cardImg ? (
                <div className="lip-link-card-media">
                  <img src={cardImg} alt="" className="lip-link-card-img" />
                </div>
              ) : null}
              <div className="lip-link-card-body">
                <div className="lip-link-card-domain">
                  {domainFromUrl(linkUrl)}
                </div>
                <div className="lip-link-card-title">{cardTitle}</div>
                {cardDesc ? (
                  <div className="lip-link-card-desc">{cardDesc}</div>
                ) : null}
              </div>
            </a>
          )}

          {/* HASHTAGS */}
          {post.hashtags?.length ? (
            <div className="lip-hashtags">
              {post.hashtags.map(tag => (
                <span key={tag} className="lip-tag">
                  {tag}
                </span>
              ))}
            </div>
          ) : null}

          {/* IMAGE (optional, separate from link card) */}
          {hasPostImage && (
            <section className="lip-media-wrap">
              <img
                src={postImgSrc}
                alt={post.image?.alt || ''}
                className="lip-media"
              />
            </section>
          )}

          {/* COUNTERS */}
          <div className="lip-counters">
            <div className="lip-counters-left">
              <div className="lip-reaction-bubbles" aria-hidden="true">
                <span className="lip-bubble lip-like">
                  <svg viewBox="0 0 24 24" className="lip-bubble-icon">
                    <path
                      d="M2 10.5A2.5 2.5 0 0 1 4.5 8H9l2.7-4.5c.5-.8 1.6-1.1 2.4-.6.7.5 1 1.4.7 2.2L13.7 8H19a2 2 0 0 1 2 2v1.5a5.5 5.5 0 0 1-5.5 5.5H8.8c-.4 0-.8.2-1 .6l-.6 1.2c-.2.4-.6.7-1.1.7H4.5A2.5 2.5 0 0 1 2 17.5v-7Z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="lip-bubble lip-celebrate">
                  <svg viewBox="0 0 24 24" className="lip-bubble-icon">
                    <path
                      d="M2 21l7-3 8-8-6-6-8 8-3 7zm11-14 4 4 3-3-4-4-3 3z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
                <span className="lip-bubble lip-insightful">
                  <svg viewBox="0 0 24 24" className="lip-bubble-icon">
                    <path
                      d="M9 21h6v-2H9v2zm3-19a7 7 0 0 0-4 12.9V17h8v-2.1A7 7 0 0 0 12 2z"
                      fill="currentColor"
                    />
                  </svg>
                </span>
              </div>
              <span>{Intl.NumberFormat().format(reactions)}</span>
            </div>
            <div className="lip-counters-right">
              <span>{Intl.NumberFormat().format(comments)} comments</span>
              <span>{Intl.NumberFormat().format(reposts)} reposts</span>
            </div>
          </div>
          <hr className="lip-divider" />

          {/* ACTIONS (static UI) */}
          <div className="lip-actions">
            <button className="lip-action">
              <svg viewBox="0 0 16 16" className="lip-action-icon">
                <path d="M12.91 7l-2.25-2.57a8.21 8.21 0 01-1.5-2.55L9 1.37A2.08 2.08 0 007 0a2.08 2.08 0 00-2.06 2.08v1.17a5.81 5.81 0 00.31 1.89l.28.86H2.38A1.47 1.47 0 001 7.47a1.45 1.45 0 00.64 1.21 1.48 1.48 0 00-.37 2.06 1.54 1.54 0 00.62.51h.05a1.6 1.6 0 00-.19.71A1.47 1.47 0 003 13.42v.1A1.46 1.46 0 004.4 15h4.83a5.61 5.61 0 002.48-.58l1-.42H14V7zM12 12.11l-1.19.52a3.59 3.59 0 01-1.58.37H5.1a.55.55 0 01-.53-.4l-.14-.48-.49-.21a.56.56 0 01-.34-.6l.09-.56-.42-.42a.56.56 0 01-.09-.68L3.55 9l-.4-.61A.28.28 0 013.3 8h5L7.14 4.51a4.15 4.15 0 01-.2-1.26V2.08A.09.09 0 017 2a.11.11 0 01.08 0l.18.51a10 10 0 001.9 3.24l2.84 3z" />
              </svg>
              <span>Like</span>
            </button>
            <button className="lip-action">
              <svg viewBox="0 0 16 16" className="lip-action-icon">
                <path d="M5 8h5v1H5zm11-.5v.08a6 6 0 01-2.75 5L8 16v-3H5.5A5.51 5.51 0 010 7.5 5.62 5.62 0 015.74 2h4.76A5.5 5.5 0 0116 7.5zm-2 0A3.5 3.5 0 0010.5 4H5.74A3.62 3.62 0 002 7.5 3.53 3.53 0 005.5 11H10v1.33l2.17-1.39A4 4 0 0014 7.58zM5 7h6V6H5z" />
              </svg>
              <span>Comment</span>
            </button>
            <button className="lip-action">
              <svg viewBox="0 0 16 16" className="lip-action-icon">
                <path d="M4 10H2V5c0-1.66 1.34-3 3-3h3.85L7.42 0h2.44L12 3 9.86 6H7.42l1.43-2H5c-.55 0-1 .45-1 1v5zm8-4v5c0 .55-.45 1-1 1H7.15l1.43-2H6.14L4 13l2.14 3h2.44l-1.43-2H11c1.66 0 3-1.34 3-3V6h-2z" />
              </svg>
              <span>Repost</span>
            </button>
            <button className="lip-action">
              <svg viewBox="0 0 16 16" className="lip-action-icon">
                <path d="M14 2L0 6.67l5 2.64 5.67-3.98L6.7 11l2.63 5L14 2z" />
              </svg>
              <span>Send</span>
            </button>
          </div>
          <hr className="lip-divider" />
        </article>
      </main>
    </>
  );
}
